import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import { DependentDto as DependentFamilyDto } from '../../../theSource/features/FamilyMembers/types';
import {
  useBenefitPlansQuery,
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
} from 'enrollment/queries';
import {
  BenefitPlanDto,
  BenefitPlansDto,
  DependentDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { useConfigurationQuery } from 'theSource/queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { selectLifeEventCompleted } from 'enrollment/services';
import { usePlanInfoQuery } from 'enrollment/queries/usePlanInformationQuery';

export interface BenefitDataState {
  state: {
    currentBenefitCategory: string;
    benefitData?: BenefitPlansDto;
    pendedBenefitData?: BenefitPlansDto;
    contextLookup?: string;
    fromReview?: boolean;
    benefitId?: number;
    preElectedPlan?: boolean;
  };
}

export const useOliveAdvisorChatQuery = () => {
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const { configuration } = useConfigurationQuery();

  const location = useLocation() as BenefitDataState;
  const benefit = location.state?.benefitData?.benefit;
  const benefitData = location.state?.benefitData;
  const contextLookup = location.state?.contextLookup;
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const lifeEventCompleted = useSelector(selectLifeEventCompleted);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);

  const { dependentsData } = useEnrollmentFamilyQuery({
    lumosEnabled: true,
  });
  /* eslint-disable-next-line */
  let filteredPlans: BenefitPlanDto[] = [];
  const apiHost = configuration?.apiHost;

  const filteredDependents = () => {
    if (benefitData?.coverage) {
      return (
        benefitData.coverage.req_dependents?.map(
          (dependent: DependentDto) => dependent?.dependent_id
        ) || []
      );
    }
    return [];
  };

  const requestedDependentIds = filteredDependents();

  const [selectedDependentIds, setSelectedDependentIds] = useState(
    requestedDependentIds
  );

  const { planInfoData } = usePlanInfoQuery({
    orgPlanYearId,
    enrollmentContext,
    lifeEventCompleted,
    orgPlanId: filteredPlans[0]?.plan_id,
    enabled:
      Boolean(orgPlanYearId) &&
      Boolean(enrollmentContext) &&
      !benefit?.dependents_allowed &&
      filteredPlans.length > 0,
  });

  const orgBenefitId = benefitData?.benefit?.org_benefit_id;

  const { plansData } = useBenefitPlansQuery({
    orgBenefitId,
    enrollmentContextType: contextLookup,
    dependentIds: selectedDependentIds,
    enabled: Boolean(orgBenefitId) && Boolean(contextLookup),
    lumosApiHost: configuration?.apiHost,
    keepPreviousData: true,
    includeContentDetails: true,
    includeEligibleDependentsData: true,
  });

  const plans = plansData?.data?.plans || [];
  filteredPlans = plansData?.data?.plans || [];
  const matchCoverageData = plansData?.data.match_coverage_data;
  const matchCoverageDependents =
    matchCoverageData?.match_coverage_req_dep_ids || [];
  const matchDependentsExist = matchCoverageDependents?.length > 0;

  const initPlanLoaded = Boolean(plansData !== undefined);
  useEffect(() => {
    if (Boolean(matchCoverageDependents?.length > 0)) {
      setSelectedDependentIds(matchCoverageDependents);
    } else if (Boolean(plansData !== undefined) && !benefitData?.coverage) {
      if (benefit?.dependents_allowed && initPlanLoaded) {
        const dependents = dependentsData?.data?.dependents
          ?.filter((dependent: DependentFamilyDto) => {
            return plansData?.data.plans?.some((plan) =>
              plan?.allowed_dependent_ids?.includes(dependent?.id)
            );
          })
          ?.map((dependent) => dependent?.id);
        if (dependents) {
          setSelectedDependentIds(dependents);
        }
      }
    }
    // eslint-disable-next-line
  }, [matchDependentsExist, initPlanLoaded]);

  const queryOlive = async (query: string): Promise<string> => {
    if (!benefitData?.benefit.org_benefit_id) {
      return Promise.reject('No org_benefit_id found');
    }

    try {
      const response = await axios.post(
        `${apiHost}/v2/self_service/olive_advisor/submit_query`,
        {
          user_query: query,
          subscriber_id: subscriber?.data?.id.value,
          org_benefit_id: benefitData?.benefit.org_benefit_id,
          dependent_ids: selectedDependentIds,
          enrollment_context_type: enrollmentContext,
        }
      );

      const {
        olive_response: oliveResponse,
        recommended_plan_id: recommendedPlanId,
      } = response.data.data;

      const orgBenefitId = benefitData?.benefit.org_benefit_id;

      // Get current recommendations from session storage
      const savedRecommendations: {
        orgBenefitId: number;
        oliveRecommendation: {
          oliveResponse: string;
          recommendedPlanId: number;
        };
      }[] = JSON.parse(sessionStorage.getItem('oliveRecommendations') || '[]');

      // Find the current recommendation for this orgBenefitId
      const currentRecommendation = savedRecommendations.find(
        (rec) => rec.orgBenefitId === orgBenefitId
      );

      // Check if the recommendedPlanId is unchanged
      if (
        currentRecommendation?.oliveRecommendation?.recommendedPlanId ===
        Number(recommendedPlanId)
      ) {
        return oliveResponse; // Return the response without updating storage
      }

      // Update or add the new recommendation
      const updatedRecommendations = savedRecommendations.map((rec) =>
        rec.orgBenefitId === orgBenefitId
          ? {
              orgBenefitId,
              oliveRecommendation: {
                oliveResponse,
                recommendedPlanId: Number(recommendedPlanId),
              },
            }
          : rec
      );

      if (!currentRecommendation) {
        updatedRecommendations.push({
          orgBenefitId,
          oliveRecommendation: {
            oliveResponse,
            recommendedPlanId: Number(recommendedPlanId),
          },
        });
      }

      // Save the updated array back to session storage
      sessionStorage.setItem(
        'oliveRecommendations',
        JSON.stringify(updatedRecommendations)
      );

      return oliveResponse;
    } catch (error) {
      console.error('Error fetching recommendation:', error);
      throw error;
    }
  };

  return {
    queryOlive,
    plans,
    planInfoData,
    selectedDependentIds,
    apiHost,
    subscriber,
    benefitData,
  };
};
